import * as React from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  createSampleSetupItem,
  deleteSampleSetupItem,
  getSampleSetupItems,
  updateSampleSetupItem,
} from "../../../../services/sampleSetupService";
import { showErrorAlert } from "../../../ui/utils/AlertUtils";

const EMPTY_CONTAINER = {
  name: "",
  type: "",
  size: "",
  sizeUnit: "",
};

const sortContainers = (containers) =>
  [...containers].sort((left, right) =>
    String(left.name || "").localeCompare(String(right.name || "")),
  );

const toContainerDraft = (container = EMPTY_CONTAINER) => ({
  name: container.name || "",
  type: container.type || "",
  size: container.size ?? "",
  sizeUnit: container.sizeUnit ?? container.size_unit ?? "",
});

const toContainerPayload = (container, studyId) => ({
  name: container.name.trim(),
  type: container.type.trim() || null,
  size: container.size === "" ? null : Number(container.size),
  sizeUnit: container.sizeUnit.trim() || null,
  studyId,
});

export const AnalysisContainerSettings = ({ studyId }) => {
  const { t } = useTranslation();
  const [containers, setContainers] = React.useState([]);
  const [draft, setDraft] = React.useState(EMPTY_CONTAINER);
  const [editing, setEditing] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const loadContainers = React.useCallback(async () => {
    if (!studyId) return;

    setIsLoading(true);
    try {
      const nextContainers = await getSampleSetupItems(
        "analysisContainers",
        studyId,
      );
      setContainers(sortContainers(nextContainers || []));
    } catch (error) {
      console.error("Failed to load analysis containers:", error);
      showErrorAlert(
        t("analysisContainersLoadFailed", "Failed to load containers."),
      );
    } finally {
      setIsLoading(false);
    }
  }, [studyId, t]);

  React.useEffect(() => {
    loadContainers();
  }, [loadContainers]);

  const updateDraft = (field, value) => {
    setDraft((currentDraft) => ({ ...currentDraft, [field]: value }));
  };

  const updateEditing = (field, value) => {
    setEditing((currentEditing) => ({ ...currentEditing, [field]: value }));
  };

  const validateContainer = (container) => {
    if (!container.name.trim()) {
      showErrorAlert(t("nameRequired", "Name is required."));
      return false;
    }

    if (
      container.size !== "" &&
      (!Number.isFinite(Number(container.size)) || Number(container.size) < 0)
    ) {
      showErrorAlert(
        t("containerSizeInvalid", "Container size must be a positive number."),
      );
      return false;
    }

    return true;
  };

  const createContainer = async (event) => {
    event.preventDefault();
    if (!validateContainer(draft)) return;

    setIsSaving(true);
    try {
      const createdContainer = await createSampleSetupItem(
        "analysisContainers",
        toContainerPayload(draft, studyId),
      );
      setContainers((currentContainers) =>
        sortContainers([...currentContainers, createdContainer]),
      );
      setDraft(EMPTY_CONTAINER);
    } catch (error) {
      console.error("Failed to create analysis container:", error);
      showErrorAlert(
        t("analysisContainerCreateFailed", "Failed to create container."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const saveContainer = async () => {
    if (!editing || !validateContainer(editing)) return;

    setIsSaving(true);
    try {
      const updatedContainer = await updateSampleSetupItem(
        "analysisContainers",
        editing.id,
        toContainerPayload(editing, studyId),
      );
      setContainers((currentContainers) =>
        sortContainers(
          currentContainers.map((container) =>
            String(container.id) === String(updatedContainer.id)
              ? updatedContainer
              : container,
          ),
        ),
      );
      setEditing(null);
    } catch (error) {
      console.error("Failed to update analysis container:", error);
      showErrorAlert(
        t("analysisContainerUpdateFailed", "Failed to update container."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const deleteContainer = async (container) => {
    const result = await Swal.fire({
      text: t(
        "analysisContainerDeleteConfirmation",
        "Are you sure you want to delete this container?",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("delete", "Delete"),
      cancelButtonText: t("cancel", "Cancel"),
      confirmButtonColor: "#d32f2f",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setIsSaving(true);
    try {
      await deleteSampleSetupItem("analysisContainers", container.id);
      setContainers((currentContainers) =>
        currentContainers.filter(
          (currentContainer) =>
            String(currentContainer.id) !== String(container.id),
        ),
      );
    } catch (error) {
      console.error("Failed to delete analysis container:", error);
      showErrorAlert(
        t("analysisContainerDeleteFailed", "Failed to delete container."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const fields = [
    { key: "name", label: t("containerName", "Name"), required: true },
    { key: "type", label: t("containerType", "Type") },
    {
      key: "size",
      label: t("containerSize", "Size"),
      type: "number",
    },
    { key: "sizeUnit", label: t("containerSizeUnit", "Size unit") },
  ];

  return (
    <Grid item xs={12}>
      <Paper sx={{ p: 2, borderRadius: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">
            {t("analysisContainers", "Sample Container")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.25 }}>
            {t("analysisContainersHint", "Sample storage container")}
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={createContainer}
          sx={{ mb: 2, p: 1, borderRadius: 1, bgcolor: "grey.50" }}>
          <Grid container spacing={1} alignItems="center">
            {fields.map((field) => (
              <Grid item xs={12} sm={6} md={field.key === "name" ? 3 : 2} key={field.key}>
                <TextField
                  fullWidth
                  size="small"
                  required={field.required}
                  type={field.type || "text"}
                  label={field.label}
                  value={draft[field.key]}
                  disabled={isSaving}
                  inputProps={field.type === "number" ? { min: 0, step: "any" } : undefined}
                  onChange={(event) => updateDraft(field.key, event.target.value)}
                />
              </Grid>
            ))}
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={isSaving}>
                {t("add", "Add")}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead
                sx={{
                  "& .MuiTableCell-head": {
                    fontWeight: 700,
                  },
                }}>
                <TableRow>
                  {fields.map((field) => (
                    <TableCell key={field.key}>{field.label}</TableCell>
                  ))}
                  <TableCell align="right">{t("actions", "Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {containers.map((container) => {
                  const isEditing =
                    String(editing?.id) === String(container.id);

                  return (
                    <TableRow key={container.id}>
                      {fields.map((field) => (
                        <TableCell key={field.key}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              required={field.required}
                              type={field.type || "text"}
                              value={editing[field.key]}
                              disabled={isSaving}
                              inputProps={field.type === "number" ? { min: 0, step: "any" } : undefined}
                              onChange={(event) =>
                                updateEditing(field.key, event.target.value)
                              }
                            />
                          ) : (
                            container[field.key] ?? "-"
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {isEditing ? (
                          <>
                            <IconButton
                              size="small"
                              aria-label={t("save", "Save")}
                              disabled={isSaving}
                              onClick={saveContainer}>
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label={t("cancel", "Cancel")}
                              disabled={isSaving}
                              onClick={() => setEditing(null)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              aria-label={t("edit", "Edit")}
                              disabled={isSaving}
                              onClick={() =>
                                setEditing({
                                  id: container.id,
                                  ...toContainerDraft(container),
                                })
                              }>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label={t("delete", "Delete")}
                              disabled={isSaving}
                              onClick={() => deleteContainer(container)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {containers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t("noItems", "No items")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Grid>
  );
};
